import React, { useEffect, useMemo, useState } from 'react';
import type { DailyData } from '../../types';
import type { WidgetSize } from '../../types/dashboard';
import { fmtMoney } from '../../utils/formatters';
import useElementSize from '../../hooks/useElementSize';
import type { TimeRange } from '../../types/dashboard';
import {
  getDashboardWidgetTimeframe,
  getWidgetTimeframeTrailingDays,
  type WidgetTimeframeToken,
} from '../../lib/dashboardTimeRange';
import { getTradePnl, getTradeTimestampMs, normalizeTradeStatus } from '../../lib/trades/authoritativeTradeData';

interface WinningVsLosingWidgetProps {
  dailyData: DailyData[];
  size: WidgetSize;
  totalWinningPnl: number;
  totalLosingPnl: number;
  timeRange?: TimeRange;
  /** Authoritative trade records. When provided the widget derives winning/losing
   *  counts and gross P&L from individual closed trades instead of daily buckets. */
  trades?: any[];
}

export const WinningVsLosingWidget: React.FC<WinningVsLosingWidgetProps> = ({ dailyData, size, totalWinningPnl: propWinningPnl, totalLosingPnl: propLosingPnl, timeRange, trades }) => {
  const [timeframe, setTimeframe] = useState<WidgetTimeframeToken>('7D');

  // All hooks run unconditionally before any size-based early return so React
  // hook order stays consistent across mini / regular renders.
  const [cardARef, cardASize] = useElementSize<HTMLDivElement>();
  const [cardBRef, cardBSize] = useElementSize<HTMLDivElement>();
  const [card1Ref, card1Size] = useElementSize<HTMLDivElement>();
  const [card2Ref, card2Size] = useElementSize<HTMLDivElement>();

  useEffect(() => {
    if (timeRange) setTimeframe(getDashboardWidgetTimeframe(timeRange));
  }, [timeRange]);

  useEffect(() => {
    const handler = (e: Event) => {
      try {
        const detail = (e as CustomEvent<{ id?: string; value?: unknown }>).detail;
        if (!detail || detail.id !== 'winningVsLosing') return;
        const val = detail.value as WidgetTimeframeToken | string;
        if (val === '24H' || val === '7D' || val === '30D' || val === '12M' || val === 'YTD') setTimeframe(val);
      } catch {}
    };
    window.addEventListener('widget-timeframe:selected', handler as EventListener);
    return () => window.removeEventListener('widget-timeframe:selected', handler as EventListener);
  }, []);

  const filtered = useMemo(() => {
    if (!Array.isArray(dailyData) || dailyData.length === 0) return [] as DailyData[];
    const now = Date.now();
    const tfDays = getWidgetTimeframeTrailingDays(timeframe);
    const tfMs = tfDays * 24 * 60 * 60 * 1000;
    const startOfYearMs = (() => {
      const n = new Date();
      return new Date(n.getFullYear(), 0, 1, 0, 0, 0, 0).getTime();
    })();

    const hasDate = dailyData.some(d => (d as any).date || (d as any).t || (d as any).timestamp);
    if (hasDate) {
      return dailyData.filter(d => {
        const raw = (d as any).date ?? (d as any).t ?? (d as any).timestamp;
        const ts = typeof raw === 'number' ? raw : (typeof raw === 'string' ? Date.parse(raw) : NaN);
        if (!Number.isFinite(ts)) return false;
        if (timeframe === 'YTD') return ts >= startOfYearMs;
        return ts >= (now - tfMs);
      });
    }
    if (timeframe === 'YTD') return dailyData.slice(-Math.min(dailyData.length, 365));
    return dailyData.slice(-Math.min(dailyData.length, tfDays));
  }, [dailyData, timeframe]);

  // Trade-level stats (authoritative source). When trade records are available we
  // count individual closed trades and sum their realized P&L (never daily buckets).
  const tradeStats = useMemo(() => {
    if (!Array.isArray(trades) || trades.length === 0) return null;
    const now = Date.now();
    const tfDays = getWidgetTimeframeTrailingDays(timeframe);
    const tfMs = tfDays * 24 * 60 * 60 * 1000;
    const startOfYearMs = (() => {
      const n = new Date();
      return new Date(n.getFullYear(), 0, 1, 0, 0, 0, 0).getTime();
    })();

    let wins = 0;
    let losses = 0;
    let grossWin = 0;
    let grossLoss = 0;

    for (const trade of trades) {
      if (normalizeTradeStatus(trade) !== 'closed') continue;
      const ts = getTradeTimestampMs(trade);
      if (ts == null) continue;
      if (timeframe === 'YTD' && ts < startOfYearMs) continue;
      if (timeframe !== 'YTD' && ts < now - tfMs) continue;
      const pnl = getTradePnl(trade);
      if (!Number.isFinite(pnl)) continue;
      if (pnl > 0) {
        wins += 1;
        grossWin += pnl;
      } else if (pnl < 0) {
        losses += 1;
        grossLoss += pnl;
      }
    }

    return { wins, losses, grossWin, grossLoss };
  }, [trades, timeframe]);

  const winsCount = tradeStats ? tradeStats.wins : filtered.filter(d => d.pnl > 0).length;
  const lossesCount = tradeStats ? tradeStats.losses : filtered.filter(d => d.pnl < 0).length;
  const totalTrades = winsCount + lossesCount;
  const winPct = totalTrades > 0 ? (winsCount / totalTrades) * 100 : 0;
  const lossPct = totalTrades > 0 ? (lossesCount / totalTrades) * 100 : 0;
  const winPctStr = winPct.toFixed(1);
  const lossPctStr = lossPct.toFixed(1);
  const totalWinningPnl = tradeStats ? tradeStats.grossWin : propWinningPnl;
  const totalLosingPnl = tradeStats ? tradeStats.grossLoss : propLosingPnl;

  if (size === 'mini') {
    const cardWidth = Math.max(1, cardASize.width || cardBSize.width || 160);
    const cardHeight = Math.max(1, cardASize.height || cardBSize.height || 120);
    const baseWidth = 160; // design width for mini card
    const baseHeight = 140; // design height for mini card
    const scaleW = cardWidth / baseWidth;
    const scaleH = cardHeight / baseHeight;
    const scale = Math.max(0.55, Math.min(1.1, Math.min(scaleW, scaleH)));

  // Responsive, clamped sizes for mini cards so they scale but stay within sensible bounds
  const mainFont = Math.round(Math.max(12, Math.min(22, 16 * scale * 1.25)));
  const iconSize = Math.round(Math.max(24, Math.min(96, 48 * scale * 2)));
  const countFont = Math.round(Math.max(14, Math.min(24, 22 * scale)));
  const pctFont = Math.round(Math.max(14, Math.min(28, 20 * scale * 1.2)));
  // Label font scales relative to pctFont so percent+label shrink together when constrained
  const labelFont = Math.round(Math.max(10, Math.min(14, pctFont * 0.45)));
    return (
      // Use grid so cards reliably fit: 1 column on xs, 2 columns on sm+
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 items-stretch h-full w-full overflow-hidden px-1 md:px-2">
          <div ref={cardARef} className="flex-1 min-w-0 w-full h-full min-h-0 justify-self-stretch rounded-xl bg-white/3 dark:bg-zinc-900/60 px-2 py-2 flex flex-col items-center justify-between border border-zinc-200 dark:border-zinc-800 box-border overflow-hidden">
          <div className="w-full text-center min-w-0 shrink-0 pb-1">
            <div style={{ fontSize: `${labelFont}px` }} className="leading-none mb-0.5 text-zinc-500 dark:text-zinc-400" title="Winning P&L is the sum of profitable closed trades." data-testid="winning-pnl-label">Winning P&L</div>
            <div style={{ fontSize: `${mainFont}px`, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'normal' }} className="font-normal text-emerald-600 dark:text-emerald-400 break-words">{fmtMoney(totalWinningPnl)}</div>
          </div>

          <div className="flex-1 flex items-center justify-center min-h-0 py-1">
            <svg viewBox="0 0 24 24" preserveAspectRatio="xMidYMid meet" className="text-emerald-600 dark:text-emerald-400" style={{ width: iconSize, height: iconSize, maxWidth: '60%' }} fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 2l6 8h-4v12h-4V10H6l6-8z" fill="currentColor"/></svg>
          </div>

          <div className="w-full text-center min-w-0 shrink-0 pt-1 pb-0.5">
            <div style={{ fontSize: `${countFont}px`, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'normal' }} className="font-normal text-zinc-800 dark:text-zinc-200 break-words">{winsCount} WINS</div>
          </div>

          <div className="w-full border-t border-zinc-200 dark:border-zinc-800 mt-1 pt-1 flex items-center justify-center shrink-0">
            <div className="flex items-baseline gap-1 sm:gap-2 min-w-0 whitespace-nowrap">
              <div style={{ fontSize: `${pctFont}px`, overflow: 'hidden', textOverflow: 'ellipsis' }} className="font-bold text-emerald-600 dark:text-emerald-400 shrink whitespace-nowrap">{winPctStr}%</div>
              <div style={{ fontSize: `${labelFont}px` }} className="text-zinc-600 dark:text-zinc-400 shrink whitespace-nowrap">WIN RATE</div>
            </div>
          </div>
        </div>

  <div ref={cardBRef} className="flex-1 min-w-0 w-full h-full min-h-0 justify-self-stretch rounded-xl bg-white/3 dark:bg-zinc-900/60 px-2 py-2 flex flex-col items-center justify-between border border-zinc-200 dark:border-zinc-800 box-border overflow-hidden">
          <div className="w-full text-center min-w-0 shrink-0 pb-1">
            <div style={{ fontSize: `${labelFont}px` }} className="leading-none mb-0.5 text-zinc-500 dark:text-zinc-400" title="Losing P&L is the sum of losing closed trades." data-testid="losing-pnl-label">Losing P&L</div>
            <div style={{ fontSize: `${mainFont}px`, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'normal' }} className="font-normal text-red-600 dark:text-red-500 break-words">{fmtMoney(totalLosingPnl)}</div>
          </div>

          <div className="flex-1 flex items-center justify-center min-h-0 py-1">
                    <svg viewBox="0 0 24 24" preserveAspectRatio="xMidYMid meet" className="text-red-600 dark:text-red-500 transform rotate-180" style={{ width: iconSize, height: iconSize, maxWidth: '60%' }} xmlns="http://www.w3.org/2000/svg"><path d="M12 2l6 8h-4v12h-4V10H6l6-8z" fill="currentColor"/></svg>
                  </div>

          <div className="w-full text-center min-w-0 shrink-0 pt-1 pb-0.5">
            <div style={{ fontSize: `${countFont}px`, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'normal' }} className="font-normal text-zinc-800 dark:text-zinc-200 break-words">{lossesCount} LOSSES</div>
          </div>

          <div className="w-full border-t border-zinc-200 dark:border-zinc-800 mt-1 pt-1 flex items-center justify-center shrink-0">
            <div className="flex items-baseline gap-1 sm:gap-2 min-w-0 whitespace-nowrap">
              <div style={{ fontSize: `${pctFont}px`, overflow: 'hidden', textOverflow: 'ellipsis' }} className="font-bold text-red-600 dark:text-red-500 shrink whitespace-nowrap">{lossPctStr}%</div>
              <div style={{ fontSize: `${labelFont}px` }} className="text-zinc-600 dark:text-zinc-400 shrink whitespace-nowrap">LOSS RATE</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const cardWidth = Math.max(1, card1Size.width || card2Size.width || 280);
  const cardHeight = Math.max(1, card1Size.height || card2Size.height || 200);
  const baseWidth = 320; // design width for regular card
  const baseHeight = 240; // design height for regular card
  // Scale must satisfy both width and height to prevent overflow in 2â€“3 column layouts
  const scaleW = cardWidth / baseWidth;
  const scaleH = cardHeight / baseHeight;
  const scale = Math.max(0.6, Math.min(1.25, Math.min(scaleW, scaleH)));

  // Responsive, clamped sizes for regular cards (expanded max caps to stay proportional in 2-col layouts)
  const mainFont = Math.round(Math.max(18, Math.min(54, 32 * scale * 1.2)));
  const iconSize = Math.round(Math.max(40, Math.min(150, 64 * scale * 1.6)));
  const countFont = Math.round(Math.max(14, Math.min(28, 22 * scale * 0.95)));
  const pctFont = Math.round(Math.max(18, Math.min(48, 28 * scale * 1.15)));

  // Label font scales relative to pctFont so percent+label shrink/grow together
  const labelFontReg = Math.round(Math.max(12, Math.min(18, pctFont * 0.45)));

  return (
    // Use grid so cards reliably fit: 1 column on xs, 2 columns on sm+
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 items-stretch h-full w-full overflow-hidden px-1 md:px-2 max-w-full">
  <div ref={card1Ref} className="flex-1 min-w-0 w-full h-full min-h-0 justify-self-stretch rounded-xl bg-white/3 dark:bg-zinc-900/60 px-3 md:px-4 py-2 md:py-3 flex flex-col items-center justify-between border border-zinc-200 dark:border-zinc-800 box-border overflow-hidden">
        <div className="w-full text-center min-w-0 shrink-0 pb-1">
          <div style={{ fontSize: `${labelFontReg}px` }} className="leading-none mb-0.5 text-zinc-500 dark:text-zinc-400" title="Winning P&L is the sum of profitable closed trades. Net P&L is winning P&L plus losing P&L." data-testid="winning-pnl-label">Net Winning P&L</div>
          <div style={{ fontSize: `${mainFont}px`, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'normal' }} className="font-normal text-emerald-600 dark:text-emerald-400 break-words">{fmtMoney(totalWinningPnl)}</div>
        </div>

        <div className="flex-1 flex items-center justify-center min-h-0 py-1">
          <svg viewBox="0 0 24 24" preserveAspectRatio="xMidYMid meet" className="text-emerald-600 dark:text-emerald-400" style={{ width: iconSize, height: iconSize, maxWidth: '70%' }} fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 2l8 10h-5v10h-6V12H4l8-10z" fill="currentColor"/></svg>
        </div>

        <div className="w-full text-center min-w-0 shrink-0 pt-1 pb-1">
          <div style={{ fontSize: `${countFont}px`, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'normal' }} className="font-normal text-zinc-800 dark:text-zinc-200 break-words">{winsCount} WINS</div>
        </div>

        <div className="w-full border-t border-zinc-200 dark:border-zinc-800 mt-2 md:mt-3 pt-2 flex items-center justify-center shrink-0">
          <div className="flex items-baseline gap-2 md:gap-3 min-w-0 whitespace-nowrap">
            <div style={{ fontSize: `${pctFont}px`, overflow: 'hidden', textOverflow: 'ellipsis' }} className="font-bold text-emerald-600 dark:text-emerald-400 whitespace-nowrap">{winPctStr}%</div>
            <div style={{ fontSize: `${labelFontReg}px` }} className="text-zinc-600 dark:text-zinc-400 whitespace-nowrap">WIN RATE</div>
          </div>
        </div>
      </div>

  <div ref={card2Ref} className="flex-1 min-w-0 w-full h-full min-h-0 justify-self-stretch rounded-xl bg-white/3 dark:bg-zinc-900/60 px-3 md:px-4 py-2 md:py-3 flex flex-col items-center justify-between border border-zinc-200 dark:border-zinc-800 box-border overflow-hidden">
        <div className="w-full text-center min-w-0 shrink-0 pb-1">
          <div style={{ fontSize: `${labelFontReg}px` }} className="leading-none mb-0.5 text-zinc-500 dark:text-zinc-400" title="Losing P&L is the sum of losing closed trades. Net P&L is winning P&L plus losing P&L." data-testid="losing-pnl-label">Net Losing P&L</div>
          <div style={{ fontSize: `${mainFont}px`, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'normal' }} className="font-normal text-red-600 dark:text-red-500 break-words">{fmtMoney(totalLosingPnl)}</div>
        </div>

        <div className="flex-1 flex items-center justify-center min-h-0 py-1">
          <svg viewBox="0 0 24 24" preserveAspectRatio="xMidYMid meet" className="text-red-600 dark:text-red-500 transform rotate-180" style={{ width: iconSize, height: iconSize, maxWidth: '70%' }} xmlns="http://www.w3.org/2000/svg"><path d="M12 2l8 10h-5v10h-6V12H4l8-10z" fill="currentColor"/></svg>
        </div>

        <div className="w-full text-center min-w-0 shrink-0 pt-1 pb-1">
          <div style={{ fontSize: `${countFont}px`, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'normal' }} className="font-normal text-zinc-800 dark:text-zinc-200 break-words">{lossesCount} LOSSES</div>
        </div>

        <div className="w-full border-t border-zinc-200 dark:border-zinc-800 mt-2 md:mt-3 pt-2 flex items-center justify-center shrink-0">
          <div className="flex items-baseline gap-2 md:gap-3 min-w-0 whitespace-nowrap">
            <div style={{ fontSize: `${pctFont}px`, overflow: 'hidden', textOverflow: 'ellipsis' }} className="font-bold text-red-600 dark:text-red-500 whitespace-nowrap">{lossPctStr}%</div>
            <div style={{ fontSize: `${labelFontReg}px` }} className="text-zinc-600 dark:text-zinc-400 whitespace-nowrap">LOSS RATE</div>
          </div>
        </div>
      </div>
    </div>
  );
};
